import styles from '@/shared/components/DepositWithdraw/DepositWithdraw.module.css';
import Dropdown from '@/shared/components/Dropdown/Dropdown';
import { InputCustomEvent } from '@ionic/react';
import LabelOutInput from '@/shared/components/LabelOutInput/LabelOutInput';
import { truncateLongAddress } from '@/shared/helpers/address';
import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { WhitelistedAddress } from '@/types/whitelist';
import { Address } from '@/types';
import GenericFormInput from '@/shared/components/GenericFormInput/GenericFormInput';
import { cryptoAddressValidator } from '@cex/shared-modules';
import { useTranslation } from 'react-i18next';
import { isValidDestinationTag } from '@/shared/helpers/crypto';

type Props = {
  assetId: string;
  address: Address | undefined;
  onAddressChanged: (selectedAddress: Address, isValid: boolean) => void;
  hasTag?: boolean;
  whitelistedAddresses?: WhitelistedAddress[];
};

const AddressSelector: React.FC<Props> = ({ assetId, address, onAddressChanged, hasTag, whitelistedAddresses }) => {
  const isApp = Capacitor.isNativePlatform();
  const { t } = useTranslation();

  const [addressError, setAddressError] = useState<string>();
  const [destinationTagError, setDestinationTagError] = useState<string>();

  // depending on if the user has added a whitelist for this currency we want to show a dropdown
  // with the whitelisted addresses or an input field
  //const useWhitelist = whitelistedAddresses && whitelistedAddresses.length > 0;
  const useWhitelist = false;

  const handleDropdownAddressChange = (addressLabel: string | undefined | null) => {
    if (addressLabel) {
      const whitelistedAddress = whitelistedAddresses?.find((a) => a.label === addressLabel);
      whitelistedAddress && onAddressChanged(whitelistedAddress, true);
    }
  };

  const handleAddressChange = (newAddress: string) => {
    const nextAddress = { ...address, value: newAddress };
    const isValid = cryptoAddressValidator(nextAddress.value, assetId.toUpperCase(), 'both');

    setAddressError(!isValid ? t('error.crypto_format', { assetName: assetId.toUpperCase() }) : undefined);

    onAddressChanged(nextAddress, isValid);
  };

  const handleDestinationTagChange = (newDestinationTag: string) => {
    if (address?.value) {
      const nextAddress = { ...address, tag: newDestinationTag };
      const isValid = !nextAddress.tag || isValidDestinationTag(nextAddress.tag);

      setDestinationTagError(!isValid ? t('error.destination_tag') : undefined);

      onAddressChanged(nextAddress, isValid);
    }
  };

  return (
    <div className={styles.addressContainer}>
      <div className={isApp ? styles.addressInputStyleApp : styles.addressInputStyle}>
        {useWhitelist ? (
          <>
            <Dropdown
              label={t('wallet.withdrawals.crypto.label')}
              onChange={(event: InputCustomEvent) => handleDropdownAddressChange(event.detail.value)}
              options={whitelistedAddresses?.map((address) => ({
                label: address.label,
                value: address.label
              }))}
              disabled={whitelistedAddresses?.length === 0}
              placeholder={
                !whitelistedAddresses ? t('wallet.withdrawals.crypto.address_placeholder_no_whitelist') : undefined
              }
              value={address?.label}
            />
            {address?.value && (
              <div className={styles.spacingTop}>
                <LabelOutInput
                  label={t('wallet.withdrawals.crypto.address')}
                  value={truncateLongAddress(address.value)}
                  disabled={true}
                />
              </div>
            )}
            {hasTag && address?.tag && (
              <div className={styles.spacingTop}>
                <LabelOutInput
                  label={t('wallet.withdrawals.crypto.destination_tag')}
                  value={address.tag}
                  disabled={true}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <GenericFormInput
              displayValue={address?.value || ''}
              errorMessage={addressError}
              id={'withdraw-crypto-address'}
              inputmode="text"
              type="address"
              label={t('wallet.withdrawals.crypto.label')}
              name={'withdraw-crypto-address'}
              setValue={handleAddressChange}
              isValidUserInput={!addressError}
              showValidityInfo={true}
            />
            {hasTag && (
              <div className={styles.spacingTop}>
                <GenericFormInput
                  displayValue={address?.tag || ''}
                  errorMessage={destinationTagError}
                  id={'withdraw-crypto-destinationTag'}
                  name={'withdraw-crypto-destinationTag'}
                  inputmode="text"
                  type="address"
                  label={t('wallet.withdrawals.crypto.destination_tag')}
                  setValue={handleDestinationTagChange}
                  isValidUserInput={!destinationTagError}
                  showValidityInfo={true}
                  disabled={!address?.value || !!addressError}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AddressSelector;
